import React, { useState } from 'react';
import { apiFetch } from '../api';
import { DynamicIcon } from '../components/icons';
import { motion, AnimatePresence } from 'framer-motion';

const DiagnosticsSettings = () => {
    const [target, setTarget] = useState('');
    const [port, setPort] = useState('');
    const [loading, setLoading] = useState(false);
    const [result, setResult] = useState(null);

    const handleTest = async () => {
        if (!target) return;
        setLoading(true);
        setResult(null);

        try {
            const res = await apiFetch('/diagnostics/test-network', null, {
                method: 'POST',
                body: JSON.stringify({
                    target: target.trim(),
                    port: port ? parseInt(port, 10) : null
                })
            });

            setResult({
                type: res.status === 'success' ? 'success' : 'error',
                message: res.message,
                logical_explanation: res.logical_explanation || null,
                technical_details: res.technical_details || null
            });
        } catch (error) {
            const errorMsg = error.message || String(error);
            const isEndpointMissing = errorMsg.includes('DataSource not found') || errorMsg.includes('Not Found') || errorMsg.includes('Method Not Allowed');

            setResult({
                type: 'error',
                message: isEndpointMissing ? 'Backend Not Updated' : 'Request Failed',
                logical_explanation: isEndpointMissing
                    ? 'The backend server does not have the /diagnostics/test-network endpoint yet. Please restart your local backend server to load the new code.'
                    : 'An unexpected error occurred while sending the test request.',
                technical_details: errorMsg
            });
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="max-w-4xl mx-auto space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-500">
            {/* Header Section */}
            <div className="relative overflow-hidden rounded-2xl bg-gradient-to-r from-blue-600 to-indigo-700 p-8 text-white shadow-lg">
                <div className="relative z-10 flex items-start gap-4">
                    <div className="p-3 bg-white/10 rounded-xl backdrop-blur-sm border border-white/20 shadow-inner">
                        <DynamicIcon name="Activity" className="w-8 h-8 text-white" />
                    </div>
                    <div>
                        <h2 className="text-2xl font-bold tracking-tight">Network Diagnostics</h2>
                        <p className="mt-2 text-blue-100 max-w-2xl text-sm leading-relaxed">
                            Instantly verify network reachability from the platform's backend infrastructure.
                            Enter an IP address, DNS hostname, or full URL to test firewall rules and availability for any service, API, webserver, or database.
                        </p>
                    </div>
                </div>
                {/* Decorative background elements */}
                <div className="absolute -top-24 -right-24 w-64 h-64 bg-white/10 rounded-full blur-3xl pointer-events-none" />
                <div className="absolute -bottom-24 right-10 w-48 h-48 bg-indigo-400/20 rounded-full blur-2xl pointer-events-none" />
            </div>

            {/* Test Console Card */}
            <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-700 overflow-hidden transition-all duration-300 hover:shadow-md">
                <div className="p-6 md:p-8">
                    <div className="flex items-center gap-3 mb-6">
                        <div className="h-2 w-2 rounded-full bg-blue-500 animate-pulse" />
                        <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Reachability Test Console</h3>
                    </div>

                    <div className="flex flex-col md:flex-row gap-4 mb-6">
                        <div className="flex-grow group">
                            <label className="block text-xs font-semibold tracking-wider text-gray-500 dark:text-gray-400 uppercase mb-2 ml-1">
                                Target Address
                            </label>
                            <div className="relative">
                                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-gray-400 group-focus-within:text-blue-500 transition-colors">
                                    <DynamicIcon name="Globe" className="h-5 w-5" />
                                </div>
                                <input
                                    type="text"
                                    value={target}
                                    onChange={(e) => setTarget(e.target.value)}
                                    placeholder="e.g., https://api.example.com, 172.217.24.174:443, or mydb.local"
                                    className="block w-full pl-10 pr-3 py-3 rounded-xl border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500 transition-all shadow-sm"
                                    onKeyDown={(e) => e.key === 'Enter' && handleTest()}
                                />
                            </div>
                        </div>

                        <div className="w-full md:w-32 group">
                            <label className="block text-xs font-semibold tracking-wider text-gray-500 dark:text-gray-400 uppercase mb-2 ml-1 flex items-center justify-between">
                                Port
                                <span className="text-[10px] text-gray-400 lowercase normal-case opacity-70">(Optional)</span>
                            </label>
                            <div className="relative">
                                <input
                                    type="number"
                                    value={port}
                                    onChange={(e) => setPort(e.target.value)}
                                    placeholder="Auto"
                                    className="block w-full px-3 py-3 rounded-xl border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500 transition-all shadow-sm text-center"
                                    onKeyDown={(e) => e.key === 'Enter' && handleTest()}
                                />
                            </div>
                        </div>
                    </div>

                    <div className="flex justify-end">
                        <button
                            onClick={handleTest}
                            disabled={loading || !target}
                            className={`relative overflow-hidden flex items-center justify-center gap-2 px-6 py-3 rounded-xl text-sm font-medium text-white transition-all duration-300 shadow-md hover:shadow-lg ${loading || !target
                                    ? 'bg-gray-400 cursor-not-allowed opacity-70'
                                    : 'bg-indigo-600 hover:bg-indigo-700 hover:-translate-y-0.5'
                                }`}
                        >
                            {loading ? (
                                <>
                                    <DynamicIcon name="Loader2" className="w-5 h-5 animate-spin" />
                                    <span>Pinging Target...</span>
                                </>
                            ) : (
                                <>
                                    <DynamicIcon name="Zap" className="w-5 h-5" />
                                    <span>Execute Test</span>
                                </>
                            )}
                        </button>
                    </div>
                </div>

                {/* Results Area */}
                <AnimatePresence>
                    {result && (
                        <motion.div
                            initial={{ height: 0, opacity: 0 }}
                            animate={{ height: 'auto', opacity: 1 }}
                            exit={{ height: 0, opacity: 0 }}
                            transition={{ duration: 0.3 }}
                            className={`border-t ${result.type === 'success'
                                    ? 'bg-green-50/50 dark:bg-green-900/10 border-green-100 dark:border-green-800/30'
                                    : 'bg-red-50/50 dark:bg-red-900/10 border-red-100 dark:border-red-800/30'
                                }`}
                        >
                            <div className="p-6 md:p-8">
                                <div className="flex items-start gap-4">
                                    <div className={`mt-0.5 p-2 rounded-full shadow-sm ${result.type === 'success'
                                            ? 'bg-green-100 text-green-600 dark:bg-green-900/50 dark:text-green-400'
                                            : 'bg-red-100 text-red-600 dark:bg-red-900/50 dark:text-red-400'
                                        }`}>
                                        <DynamicIcon
                                            name={result.type === 'success' ? 'CheckCircle2' : 'XCircle'}
                                            className="w-6 h-6"
                                        />
                                    </div>
                                    <div className="flex-grow">
                                        <h4 className={`text-lg font-semibold mb-1 ${result.type === 'success' ? 'text-green-900 dark:text-green-400' : 'text-red-900 dark:text-red-400'
                                            }`}>
                                            {result.message}
                                        </h4>
                                        {result.logical_explanation && (
                                            <p className="text-sm opacity-90 mb-3">{result.logical_explanation}</p>
                                        )}
                                        {result.technical_details && (
                                            <div className="mt-2 bg-white/60 dark:bg-black/20 rounded-lg p-3 border border-black/5 dark:border-white/5 font-mono text-xs text-gray-700 dark:text-gray-300 whitespace-pre-wrap break-words shadow-inner">
                                                {result.technical_details}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </motion.div>
                    )}
                </AnimatePresence>
            </div>

            {/* Helpful tips section */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm text-gray-500 dark:text-gray-400 mt-8 px-2">
                <div className="flex items-start gap-2">
                    <DynamicIcon name="Info" className="w-5 h-5 flex-shrink-0 text-blue-400 mt-0.5" />
                    <p>If you don't specify a port, the tool will try to infer it from the URL (e.g. 443 for https) or default to port 80.</p>
                </div>
                <div className="flex items-start gap-2">
                    <DynamicIcon name="Shield" className="w-5 h-5 flex-shrink-0 text-blue-400 mt-0.5" />
                    <p>A "Connection Refused" means the server exists and is reachable, but nothing is listening on that specific port.</p>
                </div>
                <div className="flex items-start gap-2">
                    <DynamicIcon name="Clock" className="w-5 h-5 flex-shrink-0 text-blue-400 mt-0.5" />
                    <p>A "Timeout" usually indicates that a firewall or AWS Security Group is actively dropping the packets.</p>
                </div>
            </div>
        </div>
    );
};

export default DiagnosticsSettings;
